import { BackHandler } from 'react-native'

import storageUtil from '@zet/utils/storage'
import * as ImagePicker from 'expo-image-picker'
import { Moment } from 'moment'

import { getSecureRandomRange } from './crypto'

const getCategoryName = (id: number | string, isPlural = false) => {
  switch (id?.toString()) {
    case '15':
      return isPlural ? 'Credit Cards' : 'Credit Card'
    case '9':
      return isPlural ? 'Personal Loans' : 'Personal Loan'
    case '18':
      return isPlural ? 'Secured Cards' : 'Secured Card'
    case '10':
      return isPlural ? 'Business Loans' : 'Business Loan'
    case '11':
      return isPlural ? 'Instant Loans' : 'Instant Loan'
    case '16':
      return isPlural ? 'Pay Later/EMI Cards' : 'Pay Later/EMI Card'
    case '12':
      return 'Gold Loan'
    case '2':
      return 'Digital Gold'
    case '5':
      return isPlural ? 'Bank Accounts' : 'Bank Account'
    case '17':
      return isPlural ? 'Others' : 'Other'
    case '14':
      return 'Insurance'
    case '1':
      return 'Investment'
    case '3':
      return 'Payment Gateway'
    case '4':
      return 'Fastag'
    case '6':
      return isPlural ? 'Demat Accounts' : 'Demat Account'
    case '7':
      return 'Cryptocurrency'
    case '8':
      return 'Recharge & Bill Payment'
    default:
      return ''
  }
}

const generateRandomNumber = async (min = 0, max = 100) => {
  return getSecureRandomRange(min, max)
}

const NEW_INFRA_EXPERIMENT_USER_IDS: number[] = []

const MIXPANEL_SAMPLING_WHITELISTED_USER_IDS: number[] = []

const isNewInfraEnabled = async (userId: number | undefined = undefined) => {
  const useNewInfra = true
  if (!useNewInfra) return false

  if (userId && NEW_INFRA_EXPERIMENT_USER_IDS.includes(userId)) {
    storageUtil.nonAsyncSaveObject('isNewInfraEnabled', true)
    return true
  }

  const storedIsNewInfraEnabled =
    storageUtil.nonAsyncGetObject('isNewInfraEnabled')

  if (storedIsNewInfraEnabled || (await generateRandomNumber()) < 80) {
    storageUtil.nonAsyncSaveObject('isNewInfraEnabled', true)
    return true
  }

  return false
}

const isMixpanelSamplingEnabled = async (
  userId: number | undefined = undefined,
) => {
  const useMixpanelSampling = true
  if (!useMixpanelSampling) return false

  if (userId && MIXPANEL_SAMPLING_WHITELISTED_USER_IDS.includes(userId)) {
    storageUtil.saveObject('isMixpanelSamplingEnabled', true)
    return true
  }

  const storedIsMixpanelSamplingEnabled = storageUtil.getObject(
    'isMixpanelSamplingEnabled',
  )

  if (storedIsMixpanelSamplingEnabled || (await generateRandomNumber()) < 25) {
    storageUtil.saveObject('isMixpanelSamplingEnabled', true)
    return true
  }

  return false
}

const removeExtraPincodes = (pincodes: string[]) => {
  if (pincodes.length > 5) {
    while (pincodes.length > 5) {
      pincodes.pop()
    }
  }
  return pincodes
}

const removeDuplicatesInArrayInDescendingOrder = (arr: string[]) => {
  const indexObj: { [key: string]: number } = {}
  for (let i = 0; i < arr.length; i++) {
    if (!(arr[i] in indexObj)) {
      indexObj[arr[i]] = i
    } else if (indexObj[arr[i]] > i) {
      indexObj[arr[i]] = i
    } else {
      arr.splice(i, 1)
      i--
    }
  }
  return arr
}

const getTimeDifferenceInSeconds = (startTime: Moment, endTime: Moment) => {
  return endTime.diff(startTime, 'seconds')
}

const clickImage = async () => {
  // Request camera permission
  const cameraPermission = await ImagePicker.requestCameraPermissionsAsync()

  if (cameraPermission.status !== 'granted') {
    return { canceled: true, assets: [] }
  }

  const result = await ImagePicker.launchCameraAsync()
  return result
}

const pickImage = async () => {
  // Request media library permission
  const mediaPermission =
    await ImagePicker.requestMediaLibraryPermissionsAsync()

  if (mediaPermission.status !== 'granted') {
    return { canceled: true, assets: [] }
  }

  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ImagePicker.MediaTypeOptions.Images,
    allowsEditing: true,
    aspect: [4, 3],
    quality: 1,
  })

  return result
}

const addBackHandler = () => {
  const backHandler = BackHandler.addEventListener(
    'hardwareBackPress',
    () => true,
  )

  return () => backHandler.remove()
}

const getFirstFourDecimalDigits = (deviceId: string) => {
  return parseInt(deviceId.substring(0, 4), 16)
}

const helpButtonExperiment = (deviceId: string) => {
  return getFirstFourDecimalDigits(deviceId) % 10 === 0
}

const trueCallerExperiment = (deviceId: string, percent: number) => {
  return getFirstFourDecimalDigits(deviceId) % (100 / percent) === 0
}

enum ContestsName {
  'loan_mahotsav' = 'loan_mahotsav',
  'referral_contest' = 'referral_contest',
  'referral_contest_nov_jan' = 'referral_contest_nov_jan',
  'loan_leads_jackpot' = 'loan_leads_jackpot',
  'magnet_double_dhamaka' = 'magnet_double_dhamaka',
  'zet_earning_festival' = 'zet_earning_festival',
  'credit_card_mahotsav' = 'credit_card_mahotsav',
  'bharat_loans_mahotsav' = 'bharat_loans_mahotsav',
  'reactivation_campaign' = 'reactivation_campaign', // paytm first membership
  'referral_contest_feb_mar' = 'referral_contest_feb_mar',
  'ipl_campaign' = 'ipl_campaign',
  'referral_contest_may_july' = 'referral_contest_may_july',
  'savings_bank_account_contest' = 'savings_bank_account_contest',
  'sbm_zet_card_june_campaign' = 'sbm_zet_card_june_campaign',
  'hard_churn_reactivation_campaign' = 'hard_churn_reactivation_campaign',
  'referral_contest_aug_oct_new' = 'referral_contest_aug_oct_new',
  'sbm_zet_card_sept_campaign' = 'sbm_zet_card_sept_campaign',
  'sbm_zet_dhanwali_diwali_campaign' = 'sbm_zet_dhanwali_diwali_campaign',
  'referral_contest_nov_jan_new' = 'referral_contest_nov_jan_new',
  'sbm_zet_kamaai_carnival_campaign' = 'sbm_zet_kamaai_carnival_campaign',
  'referral_contest_feb_apr' = 'referral_contest_feb_apr',
}

export {
  addBackHandler,
  clickImage,
  ContestsName,
  generateRandomNumber,
  getCategoryName,
  getTimeDifferenceInSeconds,
  helpButtonExperiment,
  isMixpanelSamplingEnabled,
  isNewInfraEnabled,
  pickImage,
  removeDuplicatesInArrayInDescendingOrder,
  removeExtraPincodes,
  trueCallerExperiment,
}
